import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';

import {
    EMAIL_IN_USE_ERROR_TYPE,
    formatEmailInUseMessage,
    type GetContactProfileOutput,
    getTimeZoneOptions,
    MAX_INPUT_FIELD_LENGTH,
    resolveEmailInUseError,
    type UpdateContactProfileInput,
    useUpdateContactProfileOptions,
} from '@/entities/client';
import { useSaveThenGoBack } from '@/shared/lib/use-save-then-go-back';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { ContactPreferencesSection } from '@/shared/ui/forms/contact-preferences-section';
import { EmailPhoneFields } from '@/shared/ui/forms/email-phone-fields';
import { PersonNameFields } from '@/shared/ui/forms/person-name-fields';
import { PreferredFirstNameField } from '@/shared/ui/forms/preferred-first-name-field';
import { ProfileFormSection } from '@/shared/ui/forms/profile-form-section';
import { PronounsFields } from '@/shared/ui/forms/pronouns-fields';
import { TimeZoneField } from '@/shared/ui/forms/time-zone-field';
import { UsAddressFields } from '@/shared/ui/forms/us-address-fields';
import { SheetFooter } from '@/shared/ui/panels/sheet-panel';

import { buildEditContactProfileDefaults } from '../../lib/build-edit-contact-profile-defaults';
import { editContactProfileFormSchema } from '../../model/edit-contact-profile.schema';

const TIME_ZONE_OPTIONS = getTimeZoneOptions();

type Props = {
    profile: GetContactProfileOutput;
    onDirtyChange: (isDirty: boolean) => void;
};

export function EditContactProfileForm({ profile, onDirtyChange }: Props) {
    const goBack = useSheetStore((state) => state.goBack);

    const updateProfileMutation = useMutation(useUpdateContactProfileOptions(profile.personId));

    const form = useForm<UpdateContactProfileInput>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: zodResolver(editContactProfileFormSchema, undefined, { raw: true }),
        defaultValues: buildEditContactProfileDefaults(profile),
    });

    const { isDirty, isValid } = form.formState;

    // The cleanup disarms the shell's discard guard when this form unmounts (the contact changed or
    // the sheet closed), so a form that is gone cannot leave it armed for the next one.
    useEffect(() => {
        onDirtyChange(isDirty);
        return () => onDirtyChange(false);
    }, [isDirty, onDirtyChange]);

    // Any other failure surfaces through the mutation's own toast.
    const saveThenGoBack = useSaveThenGoBack((error) => {
        const emailInUse = resolveEmailInUseError(error);
        if (emailInUse) {
            form.setError('emailPrimary', {
                type: EMAIL_IN_USE_ERROR_TYPE,
                message: formatEmailInUseMessage(emailInUse.holder).message,
            });
        }
    });

    const onSubmit = (data: UpdateContactProfileInput) =>
        saveThenGoBack(() => updateProfileMutation.mutateAsync(data));

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 overflow-y-auto">
                    <ProfileFormSection title="Personal details">
                        <div className="grid grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-4">
                            <PersonNameFields maxLength={MAX_INPUT_FIELD_LENGTH} />
                            <PreferredFirstNameField maxLength={MAX_INPUT_FIELD_LENGTH} />
                        </div>
                    </ProfileFormSection>
                    <ProfileFormSection title="Contact information">
                        <EmailPhoneFields
                            maxLength={MAX_INPUT_FIELD_LENGTH}
                            revalidateOnErrorType={EMAIL_IN_USE_ERROR_TYPE}
                        />
                        <TimeZoneField options={TIME_ZONE_OPTIONS} />
                    </ProfileFormSection>
                    <ProfileFormSection title="Address">
                        <UsAddressFields maxLength={MAX_INPUT_FIELD_LENGTH} />
                    </ProfileFormSection>
                    <ContactPreferencesSection />
                    <ProfileFormSection title="Demographics">
                        <PronounsFields label="Pronouns" maxLength={MAX_INPUT_FIELD_LENGTH} />
                    </ProfileFormSection>
                </div>
                <SheetFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={updateProfileMutation.isPending}
                        onClick={() => goBack()}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!isDirty || !isValid || updateProfileMutation.isPending}
                    >
                        Save
                    </Button>
                </SheetFooter>
            </form>
        </FormProvider>
    );
}
